import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { prApi } from '../../utils/purchasingApi';
import { api } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import './Purchasing.css';

const PR_STATUSES = ['DRAFT','SUBMITTED','APPROVED','REJECTED','CONVERTED'];

const STATUS_LABEL = {
  DRAFT:     'Draft',
  SUBMITTED: 'Submitted',
  APPROVED:  'Approved',
  REJECTED:  'Rejected',
  CONVERTED: 'Converted',
};

const STATUS_CLASS = {
  DRAFT:     'purch-status--draft',
  SUBMITTED: 'purch-status--submitted',
  APPROVED:  'purch-status--approved',
  REJECTED:  'purch-status--rejected',
  CONVERTED: 'purch-status--converted',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-EG') : '—';
const fmtNum  = (n) => n != null ? Number(n).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

// ── PR Form ───────────────────────────────────────────────

function PrForm({ pr: initial, onClose, onSave }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    date:     initial?.date?.slice(0,10) ?? new Date().toISOString().slice(0,10),
    entityId: initial?.entityId ?? '',
    notes:    initial?.notes    ?? '',
  });
  const [lines, setLines] = useState(
    initial?.lines?.length
      ? initial.lines.map((l) => ({ ...l, estimatedUnitPrice: l.estimatedUnitPrice ?? '' }))
      : [{ lineNumber: 1, description: '', quantity: 1, unit: '', estimatedUnitPrice: '', notes: '' }]
  );
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api.get('/entities').then((r) => setEntities(r?.items ?? r ?? [])).catch(() => {});
  }, []);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const addLine = () => setLines((ls) => [
    ...ls,
    { lineNumber: ls.length + 1, description: '', quantity: 1, unit: '', estimatedUnitPrice: '', notes: '' },
  ]);

  const removeLine = (i) => setLines((ls) =>
    ls.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, lineNumber: idx + 1 }))
  );

  const validate = () => {
    const e = {};
    if (!form.date) e.date = 'Required';
    lines.forEach((l, i) => {
      if (!l.description.trim()) e[`line_${i}_desc`] = 'Required';
      if (!l.quantity || Number(l.quantity) <= 0) e[`line_${i}_qty`] = 'Must be > 0';
    });
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        date:     form.date,
        entityId: form.entityId || undefined,
        notes:    form.notes    || undefined,
        lines:    lines.map((l) => ({
          lineNumber:         l.lineNumber,
          description:        l.description.trim(),
          quantity:           Number(l.quantity),
          unit:               l.unit.trim() || undefined,
          estimatedUnitPrice: l.estimatedUnitPrice !== '' ? Number(l.estimatedUnitPrice) : undefined,
          notes:              l.notes?.trim() || undefined,
        })),
      };
      const saved = isEdit ? await prApi.update(initial.id, payload) : await prApi.create(payload);
      onSave(saved);
    } catch (err) {
      setErrors({ api: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="purch-modal-overlay">
      <div className="purch-modal purch-modal--wide">
        <div className="purch-modal-header">
          <h2 className="purch-modal-title">{isEdit ? 'Edit Purchase Request' : 'New Purchase Request'}</h2>
          <button className="purch-btn purch-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="purch-modal-body">
          {errors.api && <div className="purch-error-banner"><i className="fas fa-exclamation-circle" /> {errors.api}</div>}

          <div className="purch-form-row">
            <div className="purch-field">
              <label className="purch-label">Date *</label>
              <input className={`purch-input${errors.date ? ' purch-input--err' : ''}`} type="date"
                value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="purch-field">
              <label className="purch-label">Sub-Entity</label>
              <select className="purch-select" value={form.entityId}
                onChange={(e) => setForm((f) => ({ ...f, entityId: e.target.value }))}>
                <option value="">— all entities —</option>
                {entities.map((en) => <option key={en.id} value={en.id}>{en.code} — {en.name}</option>)}
              </select>
            </div>
          </div>

          <div className="purch-field" style={{ marginBottom: 16 }}>
            <label className="purch-label">Notes</label>
            <textarea className="purch-input purch-textarea" rows={2}
              value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>

          {/* Lines */}
          <div className="purch-lines-header">
            <span className="purch-section-title">Line Items</span>
            <button className="purch-btn purch-btn--ghost purch-btn--sm" type="button" onClick={addLine}>
              <i className="fas fa-plus" style={{ marginRight: 4 }} /> Add Line
            </button>
          </div>

          <div className="purch-lines">
            {lines.map((l, i) => (
              <div key={i} className="purch-line-row">
                <span className="purch-line-num">{l.lineNumber}</span>
                <div className="purch-field purch-field--grow">
                  <input className={`purch-input${errors[`line_${i}_desc`] ? ' purch-input--err' : ''}`}
                    placeholder="Description *"
                    value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} />
                </div>
                <div className="purch-field purch-field--sm">
                  <input className={`purch-input${errors[`line_${i}_qty`] ? ' purch-input--err' : ''}`}
                    type="number" min="0.0001" step="any" placeholder="Qty *"
                    value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                </div>
                <div className="purch-field purch-field--xs">
                  <input className="purch-input" placeholder="Unit"
                    value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} />
                </div>
                <div className="purch-field purch-field--sm">
                  <input className="purch-input" type="number" min="0" step="any" placeholder="Est. Price"
                    value={l.estimatedUnitPrice} onChange={(e) => setLine(i, 'estimatedUnitPrice', e.target.value)} />
                </div>
                <button className="purch-btn purch-btn--icon purch-btn--danger"
                  disabled={lines.length === 1}
                  onClick={() => removeLine(i)}>
                  <i className="fas fa-trash" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="purch-modal-footer">
          <button className="purch-btn purch-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="purch-btn purch-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <LoadingSpinner variant="button" /> : isEdit ? 'Save Changes' : 'Create PR'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PR Detail Panel ───────────────────────────────────────

function PrDetail({ pr: initial, onClose, onRefresh, canApprove, canCreate, currentUserId }) {
  const [pr, setPr] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    prApi.getById(initial.id).then(setPr).catch(() => {}).finally(() => setLoading(false));
  }, [initial.id]);

  const act = async (label, fn) => {
    setActing(label); setError(null);
    try { const res = await fn(); setPr(res); onRefresh(); }
    catch (e) { setError(e.message); }
    finally { setActing(null); }
  };

  const handleSubmit  = () => act('submit',  () => prApi.submit(pr.id));
  const handleApprove = () => act('approve', () => prApi.approve(pr.id));
  const handleReject  = () => { if (!rejectReason.trim()) return; act('reject', () => prApi.reject(pr.id, rejectReason)); setShowReject(false); };

  const estimatedTotal = pr?.lines?.reduce((s, l) =>
    s + (l.estimatedUnitPrice ? Number(l.estimatedUnitPrice) * Number(l.quantity) : 0), 0) ?? 0;

  return (
    <div className="purch-detail-overlay" onClick={onClose}>
      <div className="purch-detail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="purch-detail-header">
          <div>
            <h2 className="purch-detail-title">PR #{pr.prNumber}</h2>
            <span className={`purch-status ${STATUS_CLASS[pr.status]}`}>{STATUS_LABEL[pr.status]}</span>
          </div>
          <button className="purch-btn purch-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>

        {loading ? <LoadingSpinner variant="default" /> : (
          <div className="purch-detail-body">
            {error && <div className="purch-error-banner" style={{ marginBottom: 12 }}><i className="fas fa-exclamation-circle" /> {error}</div>}

            <div className="purch-detail-meta">
              <div><span className="purch-meta-key">Date</span> {fmtDate(pr.date)}</div>
              <div><span className="purch-meta-key">Requested by</span> {pr.requestedBy?.fullName}</div>
              <div><span className="purch-meta-key">Entity</span> {pr.entity?.name ?? 'All'}</div>
              <div><span className="purch-meta-key">Fiscal Year</span> {pr.fiscalYear?.code}</div>
              {pr.approvedBy  && <div><span className="purch-meta-key">Approved by</span> {pr.approvedBy.fullName} {pr.approvedAt ? `(${fmtDate(pr.approvedAt)})` : ''}</div>}
              {pr.rejectedBy  && <div><span className="purch-meta-key">Rejected by</span> {pr.rejectedBy.fullName}</div>}
              {pr.rejectionReason && <div><span className="purch-meta-key">Reason</span> {pr.rejectionReason}</div>}
              {pr.notes       && <div className="purch-meta-full"><span className="purch-meta-key">Notes</span> {pr.notes}</div>}
            </div>

            <table className="purch-lines-table">
              <thead>
                <tr><th>#</th><th>Description</th><th>Qty</th><th>Unit</th><th>Est. Price</th><th>Est. Total</th></tr>
              </thead>
              <tbody>
                {pr.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.lineNumber}</td>
                    <td>{l.description}</td>
                    <td>{Number(l.quantity).toLocaleString()}</td>
                    <td>{l.unit || '—'}</td>
                    <td className="purch-num">{l.estimatedUnitPrice ? fmtNum(l.estimatedUnitPrice) : '—'}</td>
                    <td className="purch-num">{l.estimatedUnitPrice ? fmtNum(Number(l.estimatedUnitPrice) * Number(l.quantity)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              {estimatedTotal > 0 && (
                <tfoot>
                  <tr><td colSpan={5} className="purch-total-label">Estimated Total</td><td className="purch-num purch-total-val">{fmtNum(estimatedTotal)}</td></tr>
                </tfoot>
              )}
            </table>

            {/* Actions */}
            <div className="purch-action-bar">
              {pr.status === 'DRAFT' && canCreate && pr.requestedById === currentUserId && (
                <button className="purch-btn purch-btn--primary" onClick={handleSubmit} disabled={!!acting}>
                  {acting === 'submit' ? <LoadingSpinner variant="button" /> : 'Submit for Approval'}
                </button>
              )}
              {pr.status === 'SUBMITTED' && canApprove && (
                <>
                  <button className="purch-btn purch-btn--success" onClick={handleApprove} disabled={!!acting}>
                    {acting === 'approve' ? <LoadingSpinner variant="button" /> : <><i className="fas fa-check" style={{ marginRight: 6 }} /> Approve</>}
                  </button>
                  <button className="purch-btn purch-btn--danger-outline" onClick={() => setShowReject(true)} disabled={!!acting}>
                    <i className="fas fa-times" style={{ marginRight: 6 }} /> Reject
                  </button>
                </>
              )}
              {pr.status === 'APPROVED' && (
                <button className="purch-btn purch-btn--primary"
                  onClick={() => navigate('/purchasing/orders/new', { state: { prId: pr.id, prNumber: pr.prNumber, prLines: pr.lines } })}>
                  <i className="fas fa-file-invoice" style={{ marginRight: 6 }} /> Create PO from this PR
                </button>
              )}
            </div>

            {showReject && (
              <div className="purch-reject-box">
                <label className="purch-label">Rejection reason *</label>
                <textarea className="purch-input purch-textarea" rows={2}
                  value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <div className="purch-reject-actions">
                  <button className="purch-btn purch-btn--ghost purch-btn--sm" onClick={() => setShowReject(false)}>Cancel</button>
                  <button className="purch-btn purch-btn--danger purch-btn--sm" onClick={handleReject} disabled={!!acting}>
                    {acting === 'reject' ? <LoadingSpinner variant="button" /> : 'Confirm Reject'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────

export default function PurchaseRequestsPage() {
  const { hasPermission, user } = useAuth();
  const canCreate  = hasPermission('purchases.pr.create');
  const canApprove = hasPermission('purchases.pr.approve');

  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]  = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const [modal, setModal]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await prApi.list({ status: statusFilter || undefined, search: search || undefined, page, limit: LIMIT });
      setData(res);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [statusFilter, search, page]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const handleSaved = (pr) => {
    setModal(null); setSelected(null);
    showToast(`PR #${pr.prNumber} saved.`);
    load();
  };

  const totalPages = Math.ceil(data.total / LIMIT);

  return (
    <div className="purch-page">
      {toast && (
        <div className={`purch-toast purch-toast--${toast.type}`}>
          <i className={`fas ${toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`} /> {toast.msg}
        </div>
      )}

      <div className="purch-page-header">
        <div className="purch-title-group">
          <h1 className="purch-page-title"><i className="fas fa-clipboard-list" style={{ marginRight: 10 }} />Purchase Requests</h1>
          <span className="purch-badge">{data.total} total</span>
        </div>
        {canCreate && (
          <button className="purch-btn purch-btn--primary" onClick={() => { setSelected(null); setModal('form'); }}>
            <i className="fas fa-plus" style={{ marginRight: 6 }} /> New PR
          </button>
        )}
      </div>

      <div className="purch-filters">
        <div className="purch-search-wrap">
          <i className="fas fa-search purch-search-icon" />
          <input className="purch-search" placeholder="Search notes or requester…"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="purch-select purch-select--sm" value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {PR_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      {loading ? <LoadingSpinner variant="fullpage" />
        : error ? <div className="purch-error-banner"><i className="fas fa-exclamation-circle" /> {error}</div>
        : data.items.length === 0 ? (
          <div className="purch-empty">
            <i className="fas fa-clipboard-list" />
            <p>{search || statusFilter ? 'No purchase requests match your filters.' : 'No purchase requests yet.'}</p>
          </div>
        ) : (
          <>
            <div className="purch-table-wrap">
              <table className="purch-table">
                <thead>
                  <tr><th>PR #</th><th>Date</th><th>Requested By</th><th>Entity</th><th>Lines</th><th>Status</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {data.items.map((pr) => (
                    <tr key={pr.id} className="purch-row" onClick={() => { setSelected(pr); setModal('detail'); }}>
                      <td className="purch-number">PR-{pr.prNumber}</td>
                      <td>{fmtDate(pr.date)}</td>
                      <td>{pr.requestedBy?.fullName}</td>
                      <td>{pr.entity?.code ?? '—'}</td>
                      <td>{pr._count?.lines ?? '—'}</td>
                      <td><span className={`purch-status ${STATUS_CLASS[pr.status]}`}>{STATUS_LABEL[pr.status]}</span></td>
                      <td className="purch-notes">{pr.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="purch-pagination">
                <button className="purch-btn purch-btn--ghost purch-btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><i className="fas fa-chevron-left" /></button>
                <span className="purch-page-info">Page {page} of {totalPages}</span>
                <button className="purch-btn purch-btn--ghost purch-btn--sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><i className="fas fa-chevron-right" /></button>
              </div>
            )}
          </>
        )}

      {modal === 'form' && (
        <PrForm pr={selected} onClose={() => { setModal(null); setSelected(null); }} onSave={handleSaved} />
      )}
      {modal === 'detail' && selected && (
        <PrDetail
          pr={selected}
          onClose={() => { setModal(null); setSelected(null); }}
          onRefresh={load}
          canApprove={canApprove}
          canCreate={canCreate}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}
